import { useEffect, useState } from 'react';
import { useFetcher, useLoaderData } from 'react-router';
import { useDebounceEffect } from 'ahooks';
import { ChevronUp, LocateFixed, Search } from 'lucide-react';

import { cn } from '@rocco/utils/cn';
import useLocalities from '@modules/ecommerce/hook/use-localities';
import useClosestLocality from '@modules/ecommerce/hook/use-closest-locality';
import { getCoordinatesByBrowser } from '@modules/location/hook/use-current-coordinates';
import formatLocality from '@modules/ecommerce/util/format-locality';
import type { Locality } from '@modules/ecommerce/type/locality';
import type { ItemShippingType } from '@modules/ecommerce/context/ItemShippingContext';
import { makeShippingOption } from '@modules/ecommerce/context/ItemShippingContext';
import { useVariantShipping } from '~/modules/product/hook/use-variant-shipping';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';

/** The drawer's own small label, as the group labels above the variants are set. */
const FIELD_LABEL = 'font-sans text-[0.8125rem] leading-none text-[#9b9b9b]';

/** The heading over each list of options. */
const LIST_HEADING = 'm-0 font-sans text-[0.875rem] font-medium text-[#323232]';

/**
 * One choice: its dot, its name, and what it costs or when it lands.
 *
 * The drawer's own row rather than the modal's ShippingOption — that one is
 * drawn in the legacy grey ramp at the modal's sizes, which is a different type
 * scale from everything else in this panel.
 */
const Option = ({
    selected,
    disabled,
    title,
    detail,
    onSelect,
}: {
    selected: boolean;
    disabled?: boolean;
    title: React.ReactNode;
    detail?: React.ReactNode;
    onSelect: () => void;
}) => (
    <button
        type="button"
        role="radio"
        aria-checked={selected}
        disabled={disabled}
        onClick={onSelect}
        className={cn(
            'flex w-full items-start gap-[0.625rem] border-0 bg-transparent px-0 py-[0.5rem] text-left',
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        )}
    >
        <span
            aria-hidden="true"
            className={cn(
                'mt-[0.125rem] flex size-[1rem] shrink-0 items-center justify-center rounded-full',
                selected
                    ? 'ring-[1.5px] ring-[#323232] ring-inset'
                    : 'ring-1 ring-[#c2c2c6] ring-inset',
            )}
        >
            {selected ? (
                <span className="size-[0.5rem] rounded-full bg-[#323232]" />
            ) : null}
        </span>
        <span className="min-w-0 flex-1">
            <span className="block font-sans text-[0.875rem] leading-[1.3] text-[#181818]">
                {title}
            </span>
            {detail ? (
                <span className="mt-[0.125rem] block font-sans text-[0.8125rem] leading-[1.3] text-[#9b9b9b]">
                    {detail}
                </span>
            ) : null}
        </span>
    </button>
);

interface DrawerShippingPanelProps {
    /** The live selection — picking one applies it, there is nothing to confirm. */
    shipping: ItemShippingType | null;
    onChange: (shipping: ItemShippingType) => void;
    onClose: () => void;
}

/**
 * The shipping picker, as the drawer draws it: where it goes, how it gets
 * there, and what that costs.
 *
 * The modal's own form re-set in the drawer's type. The address search, the
 * closest-locality lookup, the cart action and the option factory are the
 * shared ones; the presentation, and applying a pick as it is made rather than
 * on a Confirm, are this file's.
 */
export const DrawerShippingPanel = ({
    shipping,
    onChange,
    onClose,
}: DrawerShippingPanelProps) => {
    const { product, shoppingCartSummary } =
        useLoaderData<ProductPageLoaderData>();
    const { shippingMethods } = useVariantShipping(false);
    const cartFetcher = useFetcher();
    const { results, search, loading } = useLocalities();
    const { search: locate, locality: located } = useClosestLocality();

    const locality = shoppingCartSummary.Locality;
    const [query, setQuery] = useState(
        locality ? formatLocality(locality) : '',
    );
    const [open, setOpen] = useState(false);

    useEffect(() => {
        setQuery(locality ? formatLocality(locality) : '');
    }, [locality]);

    // A typed suburb or postcode, once the reader stops typing. A query that
    // already carries a comma is the formatted value we put there ourselves.
    useDebounceEffect(
        () => {
            if (query && !query.includes(',')) search(query);
        },
        [query],
        { wait: 200 },
    );

    const chooseLocality = (next: Locality) => {
        setQuery(formatLocality(next));
        setOpen(false);
        cartFetcher.submit(
            { locality: next.ID.toString() },
            {
                method: 'post',
                action: '/remix-api/ecommerce/update-shopping-cart',
            },
        );
    };

    useEffect(() => {
        if (located) chooseLocality(located);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [located]);

    const pickupLocations = product.Professional?.PickupLocations ?? [];
    const selectedDelivery = shipping?.type === 'delivery' ? shipping.ID : null;
    const selectedPickup =
        shipping?.type === 'clickAndCollect' ? shipping.ID : null;
    const busy = cartFetcher.state !== 'idle';

    return (
        <div className="flex flex-col gap-[1.25rem] rounded-[1rem] bg-[#f5f5f7] p-[1rem]">
            <div className="flex flex-col gap-[0.375rem]">
                <label className={FIELD_LABEL} htmlFor="drawer-deliver-to">
                    Deliver to
                </label>
                <div className="relative">
                    <input
                        id="drawer-deliver-to"
                        value={query}
                        onChange={(event) => {
                            setQuery(event.target.value);
                            setOpen(true);
                        }}
                        onFocus={() => setOpen(true)}
                        placeholder="Enter suburb or postcode"
                        className="h-[2.5rem] w-full rounded-[0.75rem] border-0 bg-white pr-[2.25rem] pl-[0.75rem] font-sans text-[0.875rem] text-[#181818] outline-none placeholder:text-[#c2c2c6]"
                    />
                    <Search
                        aria-hidden="true"
                        className="pointer-events-none absolute top-1/2 right-[0.75rem] size-[1rem] -translate-y-1/2 text-[#9b9b9b]"
                    />
                    {open && (results.length > 0 || loading) ? (
                        <ul className="absolute top-[calc(100%+0.25rem)] right-0 left-0 z-10 m-0 max-h-[12rem] list-none overflow-y-auto rounded-[0.75rem] bg-white p-[0.25rem] shadow-[0_8px_24px_rgba(0,0,0,0.12)]">
                            {loading && results.length === 0 ? (
                                <li className="px-[0.5rem] py-[0.375rem] font-sans text-[0.8125rem] text-[#9b9b9b]">
                                    Searching…
                                </li>
                            ) : null}
                            {results.map((option) => (
                                <li key={option.ID}>
                                    <button
                                        type="button"
                                        onClick={() => chooseLocality(option)}
                                        className="w-full cursor-pointer rounded-[0.5rem] border-0 bg-transparent px-[0.5rem] py-[0.375rem] text-left font-sans text-[0.8125rem] text-[#181818] hover:bg-[#f5f5f7]"
                                    >
                                        {formatLocality(option)}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : null}
                </div>
                <button
                    type="button"
                    onClick={() => {
                        getCoordinatesByBrowser()
                            .then((coord) => coord && locate(coord))
                            .catch(() => undefined);
                    }}
                    className="inline-flex w-fit cursor-pointer items-center gap-[0.25rem] border-0 bg-transparent p-0 font-sans text-[0.8125rem] text-[#9b9b9b] hover:underline hover:underline-offset-2"
                >
                    <LocateFixed
                        aria-hidden="true"
                        className="size-[0.875em]"
                    />
                    Use my current location
                </button>
            </div>

            {busy ? (
                <p className="m-0 font-sans text-[0.8125rem] text-[#9b9b9b]">
                    Updating options…
                </p>
            ) : (
                <>
                    {shippingMethods.length > 0 ? (
                        <div role="radiogroup" aria-label="Delivery">
                            <p className={LIST_HEADING}>Delivery</p>
                            {shippingMethods.map((method) => (
                                <Option
                                    key={`delivery-${method.ID}`}
                                    selected={method.ID === selectedDelivery}
                                    disabled={method.ShippingCost === null}
                                    title={
                                        method.ShippingCost &&
                                        !method.QuotedSeparately &&
                                        method.ShippingCost.Amount > 0
                                            ? `${method.Title} — ${method.ShippingCostText}`
                                            : method.Title
                                    }
                                    detail={
                                        method.QuotedSeparately
                                            ? 'Supplier will quote shipping after purchase'
                                            : method.DeliveryEstimateText
                                    }
                                    onSelect={() =>
                                        onChange(
                                            makeShippingOption(
                                                'delivery',
                                                method.ID,
                                                method.ShippingProvider,
                                            ),
                                        )
                                    }
                                />
                            ))}
                        </div>
                    ) : null}

                    {pickupLocations.length > 0 ? (
                        <div role="radiogroup" aria-label="Click and Collect">
                            <p className={LIST_HEADING}>Click &amp; Collect</p>
                            {pickupLocations.map((location) => (
                                <Option
                                    key={`pickup-${location.ID}`}
                                    selected={location.ID === selectedPickup}
                                    title={location.Title}
                                    detail={
                                        <>
                                            {location.PickupEstimateText}
                                            <br />
                                            {location.FormattedAddress}
                                        </>
                                    }
                                    onSelect={() =>
                                        onChange(
                                            makeShippingOption(
                                                'clickAndCollect',
                                                location.ID,
                                            ),
                                        )
                                    }
                                />
                            ))}
                        </div>
                    ) : null}
                </>
            )}

            {/* Nothing to confirm — a pick applies as it is made, so the foot
                is just the way back to the collapsed row. */}
            <button
                type="button"
                onClick={onClose}
                className="flex w-full cursor-pointer items-center justify-center gap-[0.25rem] border-0 bg-transparent py-[0.25rem] font-sans text-[0.8125rem] text-[#9b9b9b] hover:text-[#323232]"
            >
                Close
                <ChevronUp
                    aria-hidden="true"
                    className="size-[0.875em]"
                    strokeWidth={2.5}
                />
            </button>
        </div>
    );
};
